import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  FiPackage, FiTruck, FiDollarSign, FiZap, 
  FiFileText, FiMonitor, FiDroplet, FiTool, 
  FiCoffee, FiSearch, FiArrowRight, FiCheckCircle 
} from 'react-icons/fi';

const Home = () => {
  const valueProps = [
    {
      icon: FiPackage,
      title: 'Simplicity',
      description: 'One point of contact for all your supplies',
    },
    {
      icon: FiTruck,
      title: 'Reliability',
      description: 'Consistent, on-time delivery you can count on',
    },
    {
      icon: FiDollarSign,
      title: 'Cost-Effectiveness',
      description: 'Competitive pricing that saves you money',
    },
    {
      icon: FiZap,
      title: 'Agility',
      description: 'Source any product without stock limitations',
    },
  ];

  const productCategories = [
    {
      icon: FiFileText,
      title: 'Office Stationery & Supplies',
      description: 'Complete range of office essentials for your daily operations',
    },
    {
      icon: FiMonitor,
      title: 'ICT Equipment & Accessories',
      description: 'Modern technology solutions for your business needs',
    },
    {
      icon: FiDroplet,
      title: 'Cleaning & Janitorial Supplies',
      description: 'Professional cleaning products for pristine workspaces',
    },
    {
      icon: FiTool,
      title: 'Building & Construction Materials',
      description: 'Quality materials for all your construction projects',
    },
    {
      icon: FiCoffee,
      title: 'Hospitality Supplies',
      description: 'Premium equipment for hotels and restaurants',
    },
    {
      icon: FiSearch,
      title: 'Custom Procurement Solutions',
      description: 'Specialized sourcing for unique requirements',
    },
  ];

  const stats = [
    { value: '500+', label: 'Happy Clients' },
    { value: '10,000+', label: 'Products Sourced' },
    { value: '100%', label: 'On-Time Delivery Rate' },
    { value: '15+', label: 'Years Combined Experience' },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'You Request',
      description: 'Submit your supply list through our easy request system',
    },
    {
      number: '02',
      title: 'We Source',
      description: 'Find the best suppliers with competitive pricing',
    },
    {
      number: '03',
      title: 'We Consolidate',
      description: 'One order, one invoice, complete transparency',
    },
    {
      number: '04',
      title: 'We Deliver',
      description: 'Direct to your doorstep, on time, every time',
    },
  ];

  const testimonials = [
    {
      quote: 'NeoVam General Supplies has transformed our procurement process. Their reliability and competitive pricing have made them our go-to partner for all supplies.',
      author: 'Corporate Client',
      company: 'Leading Manufacturing Company',
    },
    {
      quote: 'The one-stop solution we needed! Managing multiple suppliers was a nightmare. Now, one call to NeoVam handles everything efficiently.',
      author: 'Procurement Manager',
      company: 'Educational Institution',
    },
    {
      quote: 'Exceptional service and quality products. Their ability to source specialized medical supplies quickly has been crucial for our operations.',
      author: 'Administrator',
      company: 'Healthcare Facility',
    },
  ];

  return (
    <>
      <Helmet>
        <title>NeoVam General Supplies - Your Trusted Partner in Business Essentials</title>
        <meta name="description" content="One-stop supplier of comprehensive business and institutional supplies across Tanzania. Office supplies, ICT equipment, cleaning products, and more." />
      </Helmet>

      <div className="min-h-screen pt-[120px]">
        {/* Hero Section */}
        <section className="relative py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-fade-in">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                  NeoVam General Supplies - <span className="text-gradient">Your Trusted Partner</span> in Business Essentials
                </h1>
                <p className="text-xl sm:text-2xl text-primary-600 dark:text-primary-400 font-semibold mb-4">
                  Effortless Procurement, Delivered
                </p>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                  One-stop supplier of comprehensive business and institutional supplies across Tanzania
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-primary text-white text-base font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300 touch-manipulation"
                  >
                    Request a Quote
                    <FiArrowRight />
                  </Link>
                  <Link
                    to="/products"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-primary-400 text-primary-400 dark:text-primary-300 text-base font-semibold rounded-xl hover:bg-primary-400 hover:text-white transition-all duration-300 touch-manipulation"
                  >
                    View Product Catalog
                    <FiArrowRight />
                  </Link>
                </div>
              </div>
              <div className="animate-slide-in-right hidden lg:block">
                <div className="relative">
                  <div className="aspect-[4/3] rounded-3xl overflow-hidden p-0 shadow-2xl">
                    <img
                      src="/assets/general_supplies_hero.jpg"
                      alt="NeoVam General Supplies Hero"
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value Propositions */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {valueProps.map((prop, index) => (
                <div
                  key={index}
                  className={`card text-center animate-slide-up animate-delay-${index * 100}`}
                >
                  <prop.icon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {prop.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {prop.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Categories Overview */}
        <section className="py-12 sm:py-16 md:py-20 bg-gray-50 dark:bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Our <span className="text-gradient">Product Categories</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Comprehensive supplies for every business need
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {productCategories.map((category, index) => (
                <div
                  key={index}
                  className="card hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <category.icon className="w-10 h-10 text-primary-400 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {category.description}
                  </p>
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-600 font-semibold transition-colors duration-300"
                  >
                    Learn More
                    <FiArrowRight />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center animate-fade-in">
                  <div className="text-4xl sm:text-5xl font-bold text-white mb-2">
                    {stat.value}
                  </div>
                  <div className="text-lg text-white/90">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                How <span className="text-gradient">We Work</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Simple, transparent, and efficient procurement process
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, index) => (
                <div key={index} className="relative animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="card text-center">
                    <div className="text-6xl font-bold text-primary-400/20 mb-4">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {step.description}
                    </p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <FiArrowRight className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-primary-400 w-6 h-6" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 sm:py-16 md:py-20 bg-gray-50 dark:bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                What Our <span className="text-gradient">Clients Say</span>
              </h2>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="card animate-fade-in"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <FiCheckCircle className="w-10 h-10 text-primary-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-6 italic leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {testimonial.author}
                    </div>
                    <div className="text-sm text-primary-400">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary-400 to-primary-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Ready to Simplify Your Procurement?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Get in touch today for a customized quote
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 text-lg font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300"
            >
              Contact Us Now
              <FiArrowRight />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
